import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Form,
  message,
  Statistic,
  Row,
  Col,
  Descriptions,
  Badge,
  Tooltip,
  Popconfirm,
  Typography,
  List,
  Empty,
  Drawer,
  Flex,
  Spin,
  DatePicker
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  ReloadOutlined,
  FilterOutlined,
  SearchOutlined,
  BankOutlined,
  DollarOutlined,
  SwapOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const WithdrawalApprovals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    search: '',
    status: 'pending',
    dateRange: null
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState(window.innerWidth <= 768 ? 'cards' : 'table');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setViewMode(mobile ? 'cards' : 'table');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchWithdrawals();
  }, [filters.status, filters.dateRange]);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 100,
        type: 'withdrawal'
      };

      if (filters.status) params.status = filters.status;
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].startOf('day').toISOString();
        params.endDate = filters.dateRange[1].endOf('day').toISOString();
      }

      const response = await api.getTransactions(params);
      setWithdrawals(response.data.transactions || []);
    } catch (error) {
      message.error('Failed to fetch withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (transactionId, action, reason = '') => {
    try {
      setLoading(true);
      const statusMap = {
        approve: 'approved',
        reject: 'rejected',
        processing: 'processing'
      };

      const status = statusMap[action];
      if (!status) {
        message.error('Invalid action');
        return;
      }

      await api.updateTransactionStatus(transactionId, { status, reason });
      message.success(`Withdrawal ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'updated'} successfully`);
      setRejectVisible(false);
      form.resetFields();
      fetchWithdrawals();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update withdrawal');
    } finally {
      setLoading(false);
    }
  };

  const filteredWithdrawals = useMemo(() => {
    return withdrawals.filter((withdrawal) => {
      const matchesSearch = !filters.search ||
        withdrawal.userId?.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        withdrawal.userId?.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
        withdrawal.reference?.toLowerCase().includes(filters.search.toLowerCase()) ||
        withdrawal.bankDetails?.accountNumber?.toLowerCase().includes(filters.search.toLowerCase());

      return matchesSearch;
    });
  }, [withdrawals, filters.search]);

  const totalAmount = useMemo(() => {
    return filteredWithdrawals.reduce((sum, withdrawal) => sum + (withdrawal.amount || 0), 0);
  }, [filteredWithdrawals]);

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: '_id',
      key: '_id',
      render: (text) => (
        <Text code copyable={{ text }}>
          {text?.slice(-8)}
        </Text>
      )
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'user',
      render: (user) => (
        <div>
          <div><strong>{user?.name || 'N/A'}</strong></div>
          <div><Text type="secondary">{user?.email || 'N/A'}</Text></div>
          <div><Text type="secondary">{user?.phone || 'N/A'}</Text></div>
        </div>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#f5222d' }}>
          ₹{amount?.toLocaleString()}
        </Text>
      )
    },
    {
      title: 'Bank Details',
      dataIndex: 'bankDetails',
      key: 'bankDetails',
      render: (bankDetails) => (
        <div style={{ fontSize: '12px' }}>
          <div>Acc: {bankDetails?.accountNumber}</div>
          <div>IFSC: {bankDetails?.ifscCode}</div>
          <div>Name: {bankDetails?.accountHolderName || 'N/A'}</div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          pending: 'orange',
          processing: 'blue',
          completed: 'green',
          failed: 'red'
        };
        return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Requested At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedWithdrawal(record);
                setDetailsVisible(true);
              }}
            />
          </Tooltip>
          {['pending', 'processing'].includes(record.status) && (
            <>
              {record.status === 'pending' && (
                <Tooltip title="Approve">
                  <Popconfirm
                    title="Approve this withdrawal?"
                    description="This will deduct money from user's wallet"
                    okText="Approve"
                    cancelText="Cancel"
                    onConfirm={() => handleStatusChange(record._id, 'approve')}
                  >
                    <Button type="text" icon={<CheckOutlined />} style={{ color: '#52c41a' }} />
                  </Popconfirm>
                </Tooltip>
              )}
              <Tooltip title="Reject">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  style={{ color: '#ff4d4f' }}
                  onClick={() => {
                    setSelectedWithdrawal(record);
                    setRejectVisible(true);
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  const renderCards = () => {
    if (filteredWithdrawals.length === 0) {
      return (
        <Card style={{ textAlign: 'center', padding: '32px', borderRadius: '20px' }}>
          <Empty description="No withdrawal requests" />
        </Card>
      );
    }

    return (
      <List
        dataSource={filteredWithdrawals}
        renderItem={(withdrawal) => (
          <List.Item
            key={withdrawal._id}
            style={{
              padding: '16px',
              marginBottom: '12px',
              background: 'white',
              borderRadius: '16px',
              border: '1px solid #f1f5f9',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{withdrawal.userId?.name || 'Unknown User'}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{withdrawal.userId?.email || 'No email'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Tag color={withdrawal.status === 'pending' ? 'orange' : withdrawal.status === 'processing' ? 'blue' : 'green'}>
                    {withdrawal.status?.toUpperCase()}
                  </Tag>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{withdrawal._id?.slice(-8)}</div>
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Account: {withdrawal.bankDetails?.accountNumber}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>IFSC: {withdrawal.bankDetails?.ifscCode}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ fontSize: '20px', color: '#f5222d' }}>
                  ₹{withdrawal.amount?.toLocaleString()}
                </Text>
                <Space>
                  <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => {
                    setSelectedWithdrawal(withdrawal);
                    setDetailsVisible(true);
                  }}>
                    View
                  </Button>
                  <Popconfirm
                    title="Approve this withdrawal?"
                    description="This will deduct money from user's wallet"
                    okText="Approve"
                    cancelText="Cancel"
                    onConfirm={() => handleStatusChange(withdrawal._id, 'approve')}
                  >
                    <Button type="link" size="small" icon={<CheckOutlined />} style={{ color: '#52c41a' }}>
                      Approve
                    </Button>
                  </Popconfirm>
                  <Button type="link" size="small" icon={<CloseOutlined />} style={{ color: '#ff4d4f' }} onClick={() => {
                    setSelectedWithdrawal(withdrawal);
                    setRejectVisible(true);
                  }}>
                    Reject
                  </Button>
                </Space>
              </div>
            </div>
          </List.Item>
        )}
      />
    );
  };

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 45%, #1e40af 100%)',
          borderRadius: isMobile ? '20px' : '24px',
          padding: isMobile ? '24px 20px' : '40px 32px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(30, 64, 175, 0.3)'
        }}
      >
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
        <Flex justify="space-between" align="center" wrap="wrap">
          <div style={{ zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '16px', padding: '12px' }}>
                <SwapOutlined style={{ fontSize: isMobile ? '24px' : '28px', color: 'white' }} />
              </div>
              <Title level={isMobile ? 3 : 1} style={{ margin: 0, color: 'white' }}>
                Withdrawal Approvals
              </Title>
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}>
              Review and process pending withdrawal requests
            </Text>
          </div>
          <Space size={isMobile ? 8 : 12} wrap>
            <Button icon={<ReloadOutlined />} onClick={fetchWithdrawals} loading={loading} style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '12px',
              height: isMobile ? '40px' : '44px',
              fontWeight: '600'
            }}>
              Refresh
            </Button>
          </Space>
        </Flex>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} md={8}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
          >
            <div style={{ position: 'absolute', top: -10, right: -10, fontSize: 60, opacity: 0.15 }}>⏳</div>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Pending Withdrawals</span>}
              value={filteredWithdrawals.length}
              valueStyle={{ color: 'white', fontSize: isMobile ? 18 : 26 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
          >
            <div style={{ position: 'absolute', top: -10, right: -10, fontSize: 60, opacity: 0.15 }}>💸</div>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Total Amount</span>}
              value={totalAmount}
              prefix="₹"
              precision={2}
              valueStyle={{ color: 'white', fontSize: isMobile ? 18 : 26 }}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
          >
            <div style={{ position: 'absolute', top: -10, right: -10, fontSize: 60, opacity: 0.15 }}>🏦</div>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Processing Time</span>}
              value="1-3 days"
              valueStyle={{ color: 'white', fontSize: isMobile ? 18 : 26 }}
            />
          </Card>
        </Col>
      </Row>

      {isMobile ? (
        <Card
          style={{
            marginBottom: 20,
            borderRadius: '20px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Search withdrawals by user, account, reference..."
              allowClear
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              style={{
                borderRadius: '16px',
                border: '2px solid #f1f5f9',
                background: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                fontSize: '16px',
                height: '48px'
              }}
            />
          </div>
          <Button
            block
            icon={<FilterOutlined />}
            onClick={() => setShowFilters(!showFilters)}
            style={{
              borderRadius: '16px',
              height: '48px',
              fontSize: '16px',
              fontWeight: '600',
              background: showFilters ? 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderColor: showFilters ? '#1d4ed8' : '#e2e8f0',
              color: showFilters ? 'white' : '#475569'
            }}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Card>
      ) : (
        <Card style={{ marginBottom: 24, borderRadius: '16px' }}>
          <Row gutter={16} align="middle">
            <Col span={10}>
              <Input
                placeholder="Search withdrawals..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              />
            </Col>
            <Col span={6}>
              <Select
                value={filters.status}
                onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                style={{ width: '100%' }}
              >
                <Select.Option value="">All Status</Select.Option>
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="processing">Processing</Select.Option>
                <Select.Option value="completed">Completed</Select.Option>
                <Select.Option value="failed">Failed</Select.Option>
              </Select>
            </Col>
            <Col span={8}>
              <RangePicker
                style={{ width: '100%' }}
                value={filters.dateRange}
                onChange={(dates) => setFilters((prev) => ({ ...prev, dateRange: dates }))}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Drawer
        title="Filter Withdrawals"
        placement="bottom"
        open={showFilters}
        onClose={() => setShowFilters(false)}
        height="60%"
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Select
              placeholder="Select status"
              value={filters.status}
              onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="">All Status</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="processing">Processing</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
            </Select>
          </Col>
          <Col span={24}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters((prev) => ({ ...prev, dateRange: dates }))}
            />
          </Col>
        </Row>
        <Button
          type="primary"
          block
          style={{ marginTop: 24, height: '48px', borderRadius: '16px' }}
          onClick={() => setShowFilters(false)}
        >
          Apply Filters
        </Button>
      </Drawer>

      {loading && filteredWithdrawals.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Spin size="large" />
        </div>
      ) : viewMode === 'cards' ? (
        renderCards()
      ) : (
        <Card style={{ borderRadius: '16px' }}>
          <Table
            columns={columns}
            dataSource={filteredWithdrawals}
            rowKey="_id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1100 }}
          />
        </Card>
      )}

      <Modal
        title="🏦 Withdrawal Details"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 600}
        style={{ top: isMobile ? 0 : 20 }}
      >
        {selectedWithdrawal && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Transaction ID">
              <Text code copyable={{ text: selectedWithdrawal._id }}>
                {selectedWithdrawal._id}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="User">
              <div>
                <div><strong>{selectedWithdrawal.userId?.name}</strong></div>
                <div>{selectedWithdrawal.userId?.email}</div>
                <div>{selectedWithdrawal.userId?.phone}</div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong style={{ color: '#f5222d', fontSize: '18px' }}>
                ₹{selectedWithdrawal.amount?.toLocaleString()}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Bank Details">
              <div>
                <div>Account: {selectedWithdrawal.bankDetails?.accountNumber}</div>
                <div>IFSC: {selectedWithdrawal.bankDetails?.ifscCode}</div>
                <div>Holder: {selectedWithdrawal.bankDetails?.accountHolderName}</div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={selectedWithdrawal.status === 'pending' ? 'processing' : selectedWithdrawal.status === 'completed' ? 'success' : 'warning'} text={selectedWithdrawal.status?.toUpperCase()} />
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedWithdrawal.description}
            </Descriptions.Item>
            <Descriptions.Item label="Requested At">
              {dayjs(selectedWithdrawal.createdAt).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="❌ Reject Withdrawal"
        open={rejectVisible}
        onCancel={() => {
          setRejectVisible(false);
          form.resetFields();
        }}
        onOk={() => {
          form.validateFields().then((values) => {
            handleStatusChange(selectedWithdrawal._id, 'reject', values.reason);
          });
        }}
        okText="Reject"
        okButtonProps={{ danger: true }}
        width={isMobile ? '100%' : 500}
        style={{ top: isMobile ? 0 : 20 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea rows={4} placeholder="Enter reason for rejection..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WithdrawalApprovals;
